import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  forwardRef,
  inject,
  OnInit,
  computed,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MediaUploadComponent, PartialUploadConfig, MediaFile } from '@konradkrk/ngx-media-upload';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Message } from 'primeng/message';
import { Button } from 'primeng/button';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ImageFile } from '../../../models';
import { FileTokenService } from '../../../api';
import { WIZARD_CONFIG } from '../../../config/wizard.config';

/** Aggregate upload state surfaced to the host form so it can gate navigation. */
export interface GalleryUploadState {
  /** Files still uploading or queued. */
  pending: number;
  /** Files whose upload failed. */
  failed: number;
  /** True when nothing is uploading and nothing failed. */
  settled: boolean;
}

@Component({
  selector: 'app-gallery-upload',
  standalone: true,
  imports: [MediaUploadComponent, ProgressSpinner, Message, Button, TranslocoModule],
  templateUrl: './gallery-upload.component.html',
  styleUrl: './gallery-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GalleryUploadComponent),
      multi: true,
    },
  ],
})
export class GalleryUploadComponent implements ControlValueAccessor, OnInit {
  private readonly fileTokenService = inject(FileTokenService, { optional: true });
  private readonly translocoService = inject(TranslocoService);
  private readonly config = inject(WIZARD_CONFIG);

  readonly label = input<string>('');
  readonly minImages = input<number>(1);
  readonly maxImages = input<number>(10);
  readonly disabled = input<boolean>(false);
  readonly accept = input<string[]>(['image/*']);
  readonly autoUpload = input<boolean>(true);

  readonly imagesChange = output<ImageFile[]>();

  /**
   * Emits whenever the upload state changes so the host form can block
   * navigation/submit while uploads are in progress or have failed.
   */
  readonly uploadStateChange = output<GalleryUploadState>();

  /** Internal state synced with library */
  readonly mediaFiles = signal<MediaFile[]>([]);

  /** Confirmed (server-stored) images only — the value exposed to the form. */
  readonly images = computed(() => this.mediaFilesToImageFiles(this.mediaFiles()));

  /** Total tiles currently shown (incl. uploading/failed) — for the counter UI. */
  readonly totalCount = computed(() => this.mediaFiles().length);

  /** Files still uploading or queued. */
  readonly pendingCount = computed(
    () => this.mediaFiles().filter((m) => m.status === 'uploading' || m.status === 'pending').length,
  );

  /** Files whose upload failed. */
  readonly failedCount = computed(() => this.mediaFiles().filter((m) => m.status === 'error').length);

  /** True when nothing is uploading and nothing failed. */
  readonly uploadsSettled = computed(() => this.pendingCount() === 0 && this.failedCount() === 0);

  readonly configLoading = signal(false);
  readonly configError = signal<string | null>(null);

  /** Whether using token-based upload (dev) or direct upload (prod) */
  private readonly isTokenMode = !!this.config.fileUpload?.tokenEndpoint;

  /** Flag to prevent circular updates */
  private isInternalUpdate = false;

  /** Signature of the last value we emitted, to ignore the form round-trip echo. */
  private lastEmittedSignature: string | null = null;

  /** Store variants by image id for edit mode (variants come from backend, not from upload) */
  private readonly variantsCache = new Map<string, ImageFile['variants']>();

  /** Connection config (URL + headers) — set once, doesn't change with file count */
  private readonly connectionConfig = signal<{ uploadUrl: string; headers?: Record<string, string> } | null>(null);

  /** Whether max images limit has been reached */
  readonly isAtMax = computed(() => this.mediaFiles().length >= this.maxImages());

  /** Full upload config — recomputed when file count or connection config changes */
  readonly uploadConfig = computed<PartialUploadConfig | null>(() => {
    const conn = this.connectionConfig();
    if (!conn) return null;

    const atMax = this.isAtMax();

    return {
      maxFiles: this.maxImages(),
      maxFileSize: 20 * 1024 * 1024,
      acceptedTypes: ['image/*'],
      fieldName: 'files',
      uploadUrl: conn.uploadUrl,
      ...(conn.headers ? { headers: conn.headers } : {}),
      ui: {
        layout: {
          type: 'grid',
          reorderable: true,
          showInlineDropzone: !atMax,
          grid: {
            columns: { sm: 2, md: 3, lg: 4 },
            gap: '1rem',
            tileSize: 'md',
            showPrimaryBadge: false,
          },
        },
      },
    };
  });

  async ngOnInit(): Promise<void> {
    if (this.isTokenMode && this.fileTokenService) {
      await this.loadUploadConfigFromToken();
    } else {
      this.loadDirectUploadConfig();
    }
  }

  async refreshConfig(): Promise<void> {
    if (this.isTokenMode && this.fileTokenService) {
      await this.loadUploadConfigFromToken();
    } else {
      this.loadDirectUploadConfig();
    }
  }

  /** Direct upload mode (staging/prod) - sync, no loading needed */
  private loadDirectUploadConfig(): void {
    const endpoint = this.config.fileUpload?.uploadEndpoint ?? 'files/upload';
    const uploadUrl = `${this.config.apiUrl}${endpoint}`;

    this.connectionConfig.set({ uploadUrl });
  }

  /** Token-based upload mode (dev) - async, needs loading state */
  private async loadUploadConfigFromToken(): Promise<void> {
    this.configLoading.set(true);
    this.configError.set(null);

    try {
      const { uploadUrl, headers } = await this.fileTokenService!.getUploadConfig();
      this.connectionConfig.set({ uploadUrl, headers });
    } catch {
      this.configError.set(this.translocoService.translate('objects.wizard.gallery.configError'));
    } finally {
      this.configLoading.set(false);
    }
  }

  private onChange: (value: ImageFile[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: ImageFile[]): void {
    const files = value || [];
    const signature = this.signatureOf(files);

    // Ignore the echo of our own emission. The host form binds the (confirmed-only)
    // value back via [ngModel] → writeValue; rebuilding mediaFiles from it would drop
    // tiles that are still uploading or failed. Only genuine external values rebuild.
    if (signature === this.lastEmittedSignature) {
      return;
    }
    this.lastEmittedSignature = signature;

    const mediaFiles = files.length === 0 ? [] : this.imageFilesToMediaFiles(files);
    this.isInternalUpdate = true;
    this.mediaFiles.set(mediaFiles);
    this.isInternalUpdate = false;
  }

  registerOnChange(fn: (value: ImageFile[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(_isDisabled: boolean): void {
    // handled via input
  }

  /**
   * Called when library's items change (two-way binding)
   */
  onItemsChange(files: MediaFile[]): void {
    if (this.isInternalUpdate) return;

    this.mediaFiles.set(files);
    this.emitConfirmedValue();
    this.onTouched();
    this.emitUploadState();
  }

  onUploadComplete(_files: MediaFile[]): void {
    // Items are already updated via [items]/(itemsChange); just (re)publish to the form.
    this.emitConfirmedValue();
    this.emitUploadState();
  }

  onUploadError(event: { file: MediaFile; error: Error }): void {
    console.error('Upload error:', event.file.name, event.error.message);
    // A failed upload must not stay invisible to the host form's validation.
    this.emitUploadState();
  }

  /** Publishes only server-confirmed images to the form (never local blobs). */
  private emitConfirmedValue(): void {
    const confirmed = this.mediaFilesToImageFiles(this.mediaFiles());
    this.lastEmittedSignature = this.signatureOf(confirmed);
    this.onChange(confirmed);
    this.imagesChange.emit(confirmed);
  }

  private emitUploadState(): void {
    this.uploadStateChange.emit({
      pending: this.pendingCount(),
      failed: this.failedCount(),
      settled: this.uploadsSettled(),
    });
  }

  /** Stable identity signature of a file list (id + order) for echo detection. */
  private signatureOf(files: { id: string }[]): string {
    return files.map((f) => f.id).join('|');
  }

  /**
   * Convert ImageFile[] (our format) to MediaFile[] (library format)
   */
  private imageFilesToMediaFiles(files: ImageFile[]): MediaFile[] {
    return files.map((file, index) => {
      // Cache variants for existing images (from edit mode)
      if (file.variants && file.id) {
        this.variantsCache.set(file.id, file.variants);
      }

      return {
        id: file.id,
        serverId: file.id,
        name: this.extractFileName(file.url),
        size: 0,
        type: 'image/jpeg',
        status: file.isUploading ? 'uploading' : 'success',
        progress: file.isUploading ? (file.uploadProgress || 0) : 100,
        position: index,
        url: file.url,
        previewUrl: file.variants?.thumb || file.url,
        thumbnailUrl: file.variants?.thumb || file.url,
        file: file.file,
      } as MediaFile;
    });
  }

  /**
   * Convert MediaFile[] (library format) to ImageFile[] (our format)
   */
  private mediaFilesToImageFiles(files: MediaFile[]): ImageFile[] {
    return files
      .filter((mf) => this.isConfirmed(mf))
      .map((mf) => {
        const id = mf.serverId || mf.id;
        // Try server response first (new uploads), then cache (existing images from edit mode)
        const variants = this.extractServerVariants(mf) || this.variantsCache.get(id);

        return {
          id,
          url: this.resolveServerUrl(mf),
          file: mf.file,
          isUploading: false,
          uploadProgress: 100,
          variants,
        };
      });
  }

  /** A file counts for the form only once the server has stored it. */
  private isConfirmed(mf: MediaFile): boolean {
    return mf.status === 'success' && this.resolveServerUrl(mf) !== '';
  }

  /** Real server URL of a file; never a local `blob:`/`data:` preview. */
  private resolveServerUrl(mf: MediaFile): string {
    const fromResponse = this.extractServerUrl(mf);
    if (fromResponse) return fromResponse;
    const url = mf.url;
    if (url && !url.startsWith('blob:') && !url.startsWith('data:')) return url;
    return '';
  }

  private extractServerUrl(mf: MediaFile): string {
    const resp = mf.serverResponse as Record<string, unknown> | undefined;
    if (!resp) {
      return '';
    }

    // Format: { url: "..." }
    if (typeof resp['url'] === 'string') return resp['url'];

    // Format: { files: [{ url: "..." }] } (zaklepuj CDN response)
    const files = resp['files'] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(files) && files.length > 0) {
      const file = files[0];
      if (typeof file['url'] === 'string') return file['url'];
    }

    // Format: { data: { url: "..." } }
    const data = resp['data'] as Record<string, unknown> | undefined;
    if (data && typeof data['url'] === 'string') return data['url'];

    // Format: { fileUrl: "..." } or { path: "..." }
    if (typeof resp['fileUrl'] === 'string') return resp['fileUrl'];
    if (typeof resp['path'] === 'string') return resp['path'];

    return '';
  }

  private extractServerVariants(mf: MediaFile): ImageFile['variants'] | undefined {
    const resp = mf.serverResponse as Record<string, unknown> | undefined;
    if (!resp) {
      return undefined;
    }

    // Format: { variants: { thumb, medium, large } }
    const variants = resp['variants'] as Record<string, string> | undefined;
    if (variants && typeof variants === 'object') {
      return {
        thumb: variants['thumb'],
        medium: variants['medium'],
        large: variants['large'],
      };
    }

    // Format: { files: [{ variants: { thumb, medium, large } }] } (zaklepuj CDN response)
    const files = resp['files'] as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(files) && files.length > 0) {
      const file = files[0];
      const fileVariants = file['variants'] as Record<string, string> | undefined;
      if (fileVariants && typeof fileVariants === 'object') {
        return {
          thumb: fileVariants['thumb'],
          medium: fileVariants['medium'],
          large: fileVariants['large'],
        };
      }
    }

    // Format: { data: { variants: {...} } }
    const data = resp['data'] as Record<string, unknown> | undefined;
    if (data) {
      const dataVariants = data['variants'] as Record<string, string> | undefined;
      if (dataVariants && typeof dataVariants === 'object') {
        return {
          thumb: dataVariants['thumb'],
          medium: dataVariants['medium'],
          large: dataVariants['large'],
        };
      }
    }

    return undefined;
  }

  private extractFileName(url: string): string {
    if (!url) return 'image.jpg';
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    return filename.split('?')[0] || 'image.jpg';
  }
}
